/**
 * @file AI Gallery 面板 — 工具栏：文件标题 + 复制 / 导出 / 发布
 */
import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Bubble } from 'tea-component';
import CommonBubble from '@home/components/CommonBubble';
import iconHtmlDocument from '@/pages/aigc/assets/svg/icon-html-document.svg';
import downloadIcon from '@/pages/aigc/assets/svg/agent-ds-export.svg';
import copyIcon from '@/pages/aigc/assets/svg/agent-ds-copy.svg';
import copySuccessIcon from '@/pages/aigc/assets/svg/agent-copy-success.svg';
import iconPublishSend from '@/pages/aigc/assets/svg/icon-publish-send.svg';
import fullscreenIcon from '@/pages/aigc/assets/svg/agent-full-screen.svg';

export interface IAiGalleryToolbarProps {
  /** 文件标题 */
  displayTitle: string;
  /** 流式未完成等禁用工具栏 */
  toolbarDisabled: boolean;
  /** 是否有可复制内容 */
  canCopy: boolean;
  /** 是否为代码 Tab（控制复制按钮显隐） */
  isCodeStep: boolean;
  /** 复制成功提示是否显示 */
  showCopyTips: boolean;
  /** 是否显示发布按钮（分享场景下隐藏） */
  showPublish?: boolean;
  /** 复制 */
  onCopy: () => void;
  /** 导出 HTML */
  onDownload: () => void;
  /** 打开发布弹窗 */
  onPublish?: () => void;
  /** 全屏：新页面打开 HTML 报告 */
  onFullscreen?: () => void;
  /** 是否可全屏（有预览内容时可用） */
  canFullscreen?: boolean;
}

/**
 * AI Gallery 工具栏区域（左侧文件标题 + 右侧操作按钮）
 */
export const AiGalleryToolbar: React.FC<IAiGalleryToolbarProps> = ({
  displayTitle,
  toolbarDisabled,
  canCopy,
  isCodeStep,
  showCopyTips,
  showPublish = true,
  onCopy,
  onDownload,
  onPublish,
  onFullscreen,
  canFullscreen = true,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="dba-ai-gallery-panel__toolbar">
      <div className="dba-ai-gallery-panel__toolbar-left">
        <div className="dba-ai-gallery-panel__file-title">
          <img
            src={iconHtmlDocument}
            alt=""
            aria-hidden={true}
            className="dba-ai-gallery-panel__file-icon"
          />
          <span className="dba-ai-gallery-panel__file-name">{displayTitle}</span>
        </div>
      </div>

      <div className="dba-ai-gallery-panel__toolbar-right">
        {isCodeStep && (
          <Bubble
            visible={showCopyTips}
            content={
              <div className="dba-feedback-operation-copy-contenet">
                <img src={copySuccessIcon} alt="" style={{ width: 16, height: 16, marginRight: 5 }} />
                {formatMessage({ id: 'dba_ai_gallery_copy_success' })}
              </div>
            }
          >
            <CommonBubble
              placement="top"
              dark
              content={showCopyTips ? '' : formatMessage({ id: 'dba_ai_gallery_toolbar_copy' })}
            >
              <div
                className="dba-ai-gallery-panel__toolbar-item"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={classNames('dba-ai-gallery-panel__plain-action', {
                    'dba-ai-gallery-panel__plain-action--disabled':
                      toolbarDisabled || !canCopy,
                  })}
                  disabled={toolbarDisabled || !canCopy}
                  onClick={() => void onCopy()}
                >
                  <img src={copyIcon} alt="" aria-hidden={true} />
                  <span>
                    {formatMessage({ id: 'dba_ai_gallery_toolbar_copy_label' })}
                  </span>
                </button>
              </div>
            </CommonBubble>
          </Bubble>
        )}

        <CommonBubble
          placement="top"
          dark
          content={formatMessage({ id: 'dba_ai_gallery_fullscreen_tooltip' })}
        >
          <div
            className="dba-ai-gallery-panel__toolbar-item"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={classNames('dba-ai-gallery-panel__plain-action', {
                'dba-ai-gallery-panel__plain-action--disabled':
                  toolbarDisabled || !canFullscreen,
              })}
              disabled={toolbarDisabled || !canFullscreen}
              onClick={onFullscreen}
            >
              <img src={fullscreenIcon} alt="" aria-hidden={true} />
              <span>
                {formatMessage({ id: 'dba_ai_gallery_toolbar_fullscreen_label' })}
              </span>
            </button>
          </div>
        </CommonBubble>

        <CommonBubble
          placement="top"
          dark
          content={formatMessage({ id: 'dba_ai_gallery_export_html_tooltip' })}
        >
          <div
            className="dba-ai-gallery-panel__toolbar-item"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={classNames('dba-ai-gallery-panel__plain-action', {
                'dba-ai-gallery-panel__plain-action--disabled': toolbarDisabled,
              })}
              disabled={toolbarDisabled}
              onClick={onDownload}
            >
              <img src={downloadIcon} alt="" aria-hidden={true} />
              <span>
                {formatMessage({ id: 'dba_ai_gallery_toolbar_export_label' })}
              </span>
            </button>
          </div>
        </CommonBubble>

        {showPublish && (
          <div
            className="dba-ai-gallery-panel__toolbar-item"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={classNames('dba-ai-gallery-panel__publish-btn', {
                'dba-ai-gallery-panel__publish-btn--disabled': toolbarDisabled,
              })}
              disabled={toolbarDisabled}
              onClick={onPublish}
            >
              <img src={iconPublishSend} alt="" aria-hidden={true} />
              <span>{formatMessage({ id: 'dba_ai_gallery_publish' })}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiGalleryToolbar;
